import { GraphQLClient } from 'graphql-request';
import { gql } from 'urql';
import {
  Query,
  Mutation,
  QueryFirmIntakeQuestionsArgs,
  QueryFirmClientIntakeQuestionsArgs,
  QueryFirmClientIntakeResponsesArgs,
  MutationCreateFirmIntakeQuestionArgs,
  MutationUpdateFirmIntakeQuestionArgs,
  MutationDeleteFirmIntakeQuestionArgs,
  MutationSubmitFirmClientIntakeAnswersArgs,
} from '../generated/graphql';

export const FirmIntakeQuestionFragment = gql`
  fragment FirmIntakeQuestionFragment on FirmIntakeQuestion {
    id
    changeToken
    firmID
    label
    type
    options
    required
    order
  }
`;

const firmIntakeQuestionsQuery = gql`
  ${FirmIntakeQuestionFragment}
  query FirmIntakeQuestions($firmID: ID!) {
    firmIntakeQuestions(firmID: $firmID) {
      ...FirmIntakeQuestionFragment
    }
  }
`;

export const clientGetFirmIntakeQuestions = async (client: GraphQLClient, args: QueryFirmIntakeQuestionsArgs) => {
  return (await client.request<Pick<Query, 'firmIntakeQuestions'>>(firmIntakeQuestionsQuery, args)).firmIntakeQuestions;
};

const createFirmIntakeQuestionMutation = gql`
  ${FirmIntakeQuestionFragment}
  mutation CreateFirmIntakeQuestion($input: CreateFirmIntakeQuestionInput!) {
    createFirmIntakeQuestion(input: $input) {
      ...FirmIntakeQuestionFragment
    }
  }
`;

export const clientCreateFirmIntakeQuestion = async (
  client: GraphQLClient,
  args: MutationCreateFirmIntakeQuestionArgs
) => {
  return (await client.request<Pick<Mutation, 'createFirmIntakeQuestion'>>(createFirmIntakeQuestionMutation, args))
    .createFirmIntakeQuestion;
};

const updateFirmIntakeQuestionMutation = gql`
  ${FirmIntakeQuestionFragment}
  mutation UpdateFirmIntakeQuestion($input: UpdateFirmIntakeQuestionInput!) {
    updateFirmIntakeQuestion(input: $input) {
      ...FirmIntakeQuestionFragment
    }
  }
`;

export const clientUpdateFirmIntakeQuestion = async (
  client: GraphQLClient,
  args: MutationUpdateFirmIntakeQuestionArgs
) => {
  return (await client.request<Pick<Mutation, 'updateFirmIntakeQuestion'>>(updateFirmIntakeQuestionMutation, args))
    .updateFirmIntakeQuestion;
};

const deleteFirmIntakeQuestionMutation = gql`
  mutation DeleteFirmIntakeQuestion($id: ID!, $changeToken: String!) {
    deleteFirmIntakeQuestion(id: $id, changeToken: $changeToken)
  }
`;

export const clientDeleteFirmIntakeQuestion = async (
  client: GraphQLClient,
  args: MutationDeleteFirmIntakeQuestionArgs
) => {
  return (await client.request<Pick<Mutation, 'deleteFirmIntakeQuestion'>>(deleteFirmIntakeQuestionMutation, args))
    .deleteFirmIntakeQuestion;
};

// --- Client-facing (onboarding): read the firm's questions and submit answers ---

const firmClientIntakeQuestionsQuery = gql`
  ${FirmIntakeQuestionFragment}
  query FirmClientIntakeQuestions($firmClientID: ID!) {
    firmClientIntakeQuestions(firmClientID: $firmClientID) {
      ...FirmIntakeQuestionFragment
    }
  }
`;

export const clientGetFirmClientIntakeQuestions = async (
  client: GraphQLClient,
  args: QueryFirmClientIntakeQuestionsArgs
) => {
  return (await client.request<Pick<Query, 'firmClientIntakeQuestions'>>(firmClientIntakeQuestionsQuery, args))
    .firmClientIntakeQuestions;
};

const firmClientIntakeResponsesQuery = gql`
  query FirmClientIntakeResponses($firmClientID: ID!) {
    firmClientIntakeResponses(firmClientID: $firmClientID) {
      id
      dateTime
      data {
        entries {
          key
          label
          type
          value
        }
      }
    }
  }
`;

export const clientGetFirmClientIntakeResponses = async (
  client: GraphQLClient,
  args: QueryFirmClientIntakeResponsesArgs
) => {
  return (await client.request<Pick<Query, 'firmClientIntakeResponses'>>(firmClientIntakeResponsesQuery, args))
    .firmClientIntakeResponses;
};

const submitFirmClientIntakeAnswersMutation = gql`
  mutation SubmitFirmClientIntakeAnswers($input: SubmitFirmClientIntakeAnswersInput!) {
    submitFirmClientIntakeAnswers(input: $input) {
      success
    }
  }
`;

export const clientSubmitFirmClientIntakeAnswers = async (
  client: GraphQLClient,
  args: MutationSubmitFirmClientIntakeAnswersArgs
) => {
  return (
    await client.request<Pick<Mutation, 'submitFirmClientIntakeAnswers'>>(submitFirmClientIntakeAnswersMutation, args)
  ).submitFirmClientIntakeAnswers;
};
